import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Plus, Edit, Trash2, Package, AlertCircle, 
  CheckCircle, XCircle, Minus
} from 'lucide-react';
import { inventoryAPI } from '../../api';
import toast from 'react-hot-toast';

const InventoryVariants = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingVariant, setEditingVariant] = useState(null);
  const [adjustStock, setAdjustStock] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [productRes, variantsRes] = await Promise.all([
        inventoryAPI.getProduct(productId),
        inventoryAPI.listVariants(productId)
      ]);
      setProduct(productRes.data);
      setVariants(variantsRes.data.variants);
    } catch (error) {
      toast.error('Failed to load data');
      navigate('/partners/inventory');
    } finally {
      setLoading(false);
    }
  }, [productId, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getStatusBadge = (status) => {
    const styles = {
      ACTIVE: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
      OUT_OF_STOCK: { bg: 'bg-amber-100', text: 'text-amber-700', icon: AlertCircle },
      DISABLED: { bg: 'bg-slate-100', text: 'text-slate-600', icon: XCircle }
    };
    const style = styles[status] || styles.DISABLED;
    const Icon = style.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
        <Icon className="w-3 h-3" />
        {status.replace('_', ' ')}
      </span>
    );
  };

  const handleDeleteVariant = async (variantId) => {
    if (!window.confirm('Delete this variant?')) return;

    try {
      await inventoryAPI.deleteVariant(productId, variantId);
      toast.success('Variant deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete variant');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to={`/partners/inventory/${productId}`}
          className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900">Manage Variants</h1>
          <p className="text-slate-500">{product?.product_name} ({product?.sku_id})</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-5 h-5" />
          Add Variant
        </button>
      </div>

      {/* Info */}
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-700">
        <p className="font-medium mb-1">💡 Variants represent sellable units</p>
        <p>Each variant can have its own size, color, price, and stock quantity. Track inventory accurately for marketplace readiness.</p>
      </div>

      {/* Variants Table */}
      {variants.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border border-slate-200 p-12 text-center"
        >
          <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No variants yet</h3>
          <p className="text-slate-500 mb-6">
            Add size and color variants with pricing and stock
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-5 h-5" />
            Add First Variant
          </button>
        </motion.div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">SKU Code</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Size</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Color</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-slate-600">Price</th>
                <th className="text-center px-4 py-3 text-sm font-medium text-slate-600">Stock</th>
                <th className="text-center px-4 py-3 text-sm font-medium text-slate-600">Status</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {variants.map((variant) => (
                <tr key={variant.variant_id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-900">{variant.sku_code}</td>
                  <td className="px-4 py-3 text-slate-600">{variant.size || '-'}</td>
                  <td className="px-4 py-3 text-slate-600">{variant.color || '-'}</td>
                  <td className="px-4 py-3 text-right text-slate-900">
                    ₹{variant.price.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => setAdjustStock({ variant, amount: -1 })}
                        className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className={`font-medium ${variant.stock_available > 0 ? 'text-slate-900' : 'text-red-600'}`}>
                        {variant.stock_available}
                      </span>
                      <button
                        onClick={() => setAdjustStock({ variant, amount: 1 })}
                        className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    {variant.stock_reserved > 0 && (
                      <span className="text-xs text-slate-400">({variant.stock_reserved} reserved)</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {getStatusBadge(variant.status)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setEditingVariant(variant)}
                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteVariant(variant.variant_id)}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between pt-4 border-t border-slate-100">
        <Link
          to={`/partners/inventory/${productId}/images`}
          className="px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50"
        >
          ← Manage Images
        </Link>
        <Link
          to={`/partners/inventory/${productId}/share-links`}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Share Links →
        </Link>
      </div>

      {/* Add/Edit Variant Modal */}
      {(showAddModal || editingVariant) && (
        <VariantModal
          productId={productId}
          variant={editingVariant}
          onClose={() => {
            setShowAddModal(false);
            setEditingVariant(null);
          }}
          onSave={() => {
            setShowAddModal(false);
            setEditingVariant(null);
            fetchData();
          }}
        />
      )}

      {/* Stock Adjustment Modal */}
      {adjustStock && (
        <StockAdjustModal
          productId={productId}
          variant={adjustStock.variant}
          initialAmount={adjustStock.amount}
          onClose={() => setAdjustStock(null)}
          onSave={() => {
            setAdjustStock(null);
            fetchData();
          }}
        />
      )}
    </div>
  );
};

// Variant Add/Edit Modal
const VariantModal = ({ productId, variant, onClose, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    sku_code: variant?.sku_code || '',
    size: variant?.size || '',
    color: variant?.color || '',
    price: variant?.price || '',
    stock_total: variant?.stock_total || 0
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.sku_code || !formData.price) {
      toast.error('SKU code and price are required');
      return;
    }

    try {
      setLoading(true);
      
      const data = {
        sku_code: formData.sku_code,
        size: formData.size || null,
        color: formData.color || null,
        price: parseFloat(formData.price),
        stock_total: parseInt(formData.stock_total) || 0
      };

      if (variant) {
        await inventoryAPI.updateVariant(productId, variant.variant_id, data);
        toast.success('Variant updated');
      } else {
        await inventoryAPI.createVariant(productId, data);
        toast.success('Variant created');
      }
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save variant');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl max-w-md w-full p-6"
      >
        <h2 className="text-xl font-bold text-slate-900 mb-4">
          {variant ? 'Edit Variant' : 'Add Variant'}
        </h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              SKU Code <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.sku_code}
              onChange={(e) => setFormData(prev => ({ ...prev, sku_code: e.target.value }))}
              placeholder="e.g., DRESS-001-S-BLU"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
              disabled={!!variant}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Size</label>
              <input
                type="text"
                value={formData.size}
                onChange={(e) => setFormData(prev => ({ ...prev, size: e.target.value }))}
                placeholder="e.g., S, M, L"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Color</label>
              <input
                type="text"
                value={formData.color}
                onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                placeholder="e.g., Blue"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Price (₹) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                placeholder="0.00"
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Stock</label>
              <input
                type="number"
                value={formData.stock_total}
                onChange={(e) => setFormData(prev => ({ ...prev, stock_total: e.target.value }))}
                placeholder="0"
                min="0"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : variant ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Stock Adjustment Modal
const StockAdjustModal = ({ productId, variant, initialAmount, onClose, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [quantity, setQuantity] = useState(initialAmount);
  const [reason, setReason] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (quantity === 0) {
      toast.error('Quantity cannot be zero');
      return;
    }

    try {
      setLoading(true);
      await inventoryAPI.adjustStock(productId, variant.variant_id, {
        quantity,
        reason: reason || null
      });
      toast.success('Stock adjusted');
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust stock');
    } finally {
      setLoading(false);
    }
  };

  const newStock = variant.stock_total + quantity;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl max-w-sm w-full p-6"
      >
        <h2 className="text-xl font-bold text-slate-900 mb-4">Adjust Stock</h2>
        <p className="text-sm text-slate-500 mb-4">
          {variant.sku_code} • Current: {variant.stock_total}
        </p>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Adjustment
            </label>
            <input
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(parseInt(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            <p className="mt-1 text-sm text-slate-500">
              Positive to add, negative to subtract
            </p>
          </div>

          <div className="bg-slate-50 rounded-lg p-3 text-center">
            <span className="text-sm text-slate-500">New Stock: </span>
            <span className={`font-bold ${newStock < 0 ? 'text-red-600' : 'text-slate-900'}`}>
              {newStock}
            </span>
          </div>

          {newStock < 0 && (
            <p className="text-sm text-red-500 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              Stock cannot be negative
            </p>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Reason (optional)
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Restocked from warehouse"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || newStock < 0}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Adjust'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default InventoryVariants;
